import {
  Button,
  Card,
  DataGrid,
  DataGridState,
  PageContainer,
  Typography,
  useToast,
} from '@sl/react-datafabric-components';
import { format, isValid } from 'date-fns';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInsightContext } from '../../../../common/context/insight-context.provider';
import { useFetchPlansQuery } from '../../../../common/redux/services/plans';
import {
  useFetchCurrentUserSubscriptionsQuery,
  useGetSubscriptionInvoicesMutation,
  useReactivateSubscriptionMutation,
  useUnsubscribeMutation,
} from '../../../../common/redux/services/subscriptions';
import {
  BillingCycle,
  formatCycleLabel,
  formatPlanCyclePrice,
  getPlanCyclePriceID,
  normalizeBillingCycle,
} from '../../../../common/subscriptions/plan-pricing';
import { capitalizeFirstLetter } from '../../../../common/utils/capitalize-first-letter';
import getErrorMessage from '../../../../common/utils/get-error-message';
import { CancelSubscriptionDialog } from './cancel-subscription-dialog';
import { SubscriptionOptionsMenu } from './options-menu';
import {
  SubscriptionInvoice,
  SubscriptionInvoicesDialog,
} from './subscription-invoices-dialog';

const inferBillingCycleFromSubscription = (
  subscription: any,
  plan: any,
): BillingCycle => {
  const metadataBillingCycle = subscription?.providerMetadata?.billingCycle;
  if (metadataBillingCycle) {
    return normalizeBillingCycle(metadataBillingCycle);
  }

  const metadataPriceID = String(
    subscription?.providerMetadata?.priceID ||
      subscription?.providerMetadata?.priceId ||
      '',
  ).trim();
  if (metadataPriceID) {
    const monthlyPriceID = getPlanCyclePriceID(plan, 'monthly');
    const yearlyPriceID = getPlanCyclePriceID(plan, 'yearly');
    if (yearlyPriceID && metadataPriceID === yearlyPriceID) return 'yearly';
    if (monthlyPriceID && metadataPriceID === monthlyPriceID) return 'monthly';
  }

  return normalizeBillingCycle(plan?.metadata?.subscription?.defaultBillingCycle);
};

const buildSelectedPlanState = (plan: any, billingCycle: BillingCycle) => ({
  id: plan?.id,
  name: plan?.name,
  roleID: plan?.roleID,
  metadata: plan?.metadata,
  billingCycle,
  priceID: getPlanCyclePriceID(plan, billingCycle),
});

export default function ManageSubscriptionPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { currentContext, currentContextAgentID } = useInsightContext();
  const { data: plansData } = useFetchPlansQuery({});

  const { data: subscriptionsData, isFetching: loadingSubscriptions } =
    useFetchCurrentUserSubscriptionsQuery(
      currentContextAgentID ? { itemID: currentContextAgentID } : {},
    );

  const [tableState, setTableState] = useState<DataGridState>({
    rows: 50,
    page: 1,
    first: 0,
    multiSort: [{ field: 'name', order: -1 }],
    filters: {
      queryString: '',
    },
  });
  const [isCancelDialogVisible, setIsCancelDialogVisible] = useState(false);
  const [isInvoicesDialogVisible, setIsInvoicesDialogVisible] = useState(false);
  const [selectedInvoicesPlanName, setSelectedInvoicesPlanName] =
    useState<string>('');
  const [selectedInvoices, setSelectedInvoices] = useState<SubscriptionInvoice[]>(
    [],
  );
  const [unsubscribeMutation, { isLoading: isCancellingSubscription }] =
    useUnsubscribeMutation();
  const [reactivateSubscription, { isLoading: isReactivatingSubscription }] =
    useReactivateSubscriptionMutation();
  const [getSubscriptionInvoices, { isLoading: isLoadingInvoices }] =
    useGetSubscriptionInvoicesMutation();

  const fullSubs = (subscriptionsData?.data || []).map((sub) => {
    const dateField = sub?.renewalDate ? 'renewalDate' : 'endDate';
    return { ...sub, dateField };
  });

  const availablePlans = useMemo(
    () =>
      [...(plansData?.data || [])]
        .filter((plan) => !plan?.isDeleted)
        .sort((a, b) => Number(a?.priority || 0) - Number(b?.priority || 0)),
    [plansData?.data],
  );

  const activeSubscription =
    fullSubs.find(
      (subscription) =>
        String(subscription?.status || '').toLowerCase() === 'active',
    ) || fullSubs[0];
  const normalizedActiveStatus = String(activeSubscription?.status || '')
    .trim()
    .toLowerCase();
  const canManageRenewal = ['active', 'paymentfailed'].includes(
    normalizedActiveStatus,
  );
  const hasScheduledCancellation =
    canManageRenewal && Boolean(activeSubscription?.endDate);

  const currentPlan =
    activeSubscription?.plan ||
    availablePlans.find((plan) => plan.id === activeSubscription?.planID);
  const currentBillingCycle =
    activeSubscription && currentPlan
      ? inferBillingCycleFromSubscription(activeSubscription, currentPlan)
      : 'monthly';

  const yearlyPriceID = currentPlan
    ? getPlanCyclePriceID(currentPlan, 'yearly')
    : undefined;
  const showYearlyUpgrade =
    Boolean(activeSubscription) &&
    Boolean(currentPlan) &&
    currentBillingCycle === 'monthly' &&
    Boolean(yearlyPriceID);

  const higherTierPlans = useMemo(() => {
    if (!currentPlan) return [];
    const currentPriority = Number(currentPlan?.priority || 0);
    return availablePlans.filter(
      (plan) => Number(plan?.priority || 0) > currentPriority,
    );
  }, [availablePlans, currentPlan]);

  const openUpgradeFlow = (plan: any, billingCycle: BillingCycle) => {
    navigate('/subscriptions/subscribe', {
      state: {
        selectedPlan: buildSelectedPlanState(plan, billingCycle),
        allowExistingSubscriptionFlow: true,
      },
    });
  };

  const onConfirmCancel = async ({
    reason,
    comment,
  }: {
    reason: string;
    comment?: string;
  }) => {
    if (!activeSubscription?.id) return;
    try {
      await unsubscribeMutation({
        id: activeSubscription.id,
        reason,
        comment,
      }).unwrap();
      setIsCancelDialogVisible(false);
      showToast(
        {
          severity: 'success',
          summary: 'Done',
          detail:
            'Auto-renewal has been canceled. Access remains active until the end of the billing cycle.',
        },
        'top-right',
      );
    } catch (error) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail: getErrorMessage(error) || 'Failed to cancel auto-renewal.',
        },
        'bottom-right',
      );
    }
  };

  const onReactivate = async () => {
    if (!activeSubscription?.id) return;
    try {
      await reactivateSubscription(activeSubscription.id).unwrap();
      showToast(
        {
          severity: 'success',
          summary: 'Done',
          detail: 'Auto-renewal has been reactivated successfully.',
        },
        'top-right',
      );
    } catch (error) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail: getErrorMessage(error) || 'Failed to reactivate subscription.',
        },
        'bottom-right',
      );
    }
  };

  const canDisplayInvoices = (subscription: any) => {
    const status = String(subscription?.status || '')
      .trim()
      .toLowerCase();
    if (['paymentfailed', 'blocked'].includes(status)) return false;
    return Boolean(subscription?.providerMetadata?.subscriptionId);
  };

  const onOpenInvoices = async (subscription: any) => {
    if (!canDisplayInvoices(subscription)) return;
    try {
      setSelectedInvoicesPlanName(
        capitalizeFirstLetter(subscription?.plan?.name || 'Subscription'),
      );
      const response = await getSubscriptionInvoices(subscription.id).unwrap();
      setSelectedInvoices(response?.data || []);
      setIsInvoicesDialogVisible(true);
    } catch (error) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail:
            getErrorMessage(error) ||
            'Could not load invoices for this subscription.',
        },
        'bottom-right',
      );
    }
  };

  return (
    <PageContainer>
      <div className='mb-4'>
        <Typography type={2} className='text-3xl font-bold mb-1'>
          {currentContext
            ? `Manage subscription for ${currentContext.name}`
            : 'Manage subscription'}
        </Typography>
        <Typography className='text-600'>
          Review your current plan and available upgrade paths.
        </Typography>
      </div>

      {activeSubscription && currentPlan && (
        <Card className='mb-4'>
          <div className='flex flex-column gap-3 md:flex-row md:justify-content-between md:align-items-center'>
            <div>
              <Typography type={3} className='font-semibold'>
                {capitalizeFirstLetter(currentPlan?.name || 'Plan')} (
                {formatCycleLabel(currentBillingCycle)})
              </Typography>
              <Typography className='text-700'>
                {formatPlanCyclePrice(currentPlan, currentBillingCycle)}
              </Typography>
            </div>

            <div className='flex flex-wrap gap-2'>
              {canManageRenewal &&
                (hasScheduledCancellation ? (
                  <Button
                    label='Reactivate subscription'
                    icon='pi pi-refresh'
                    severity='success'
                    outlined
                    loading={isReactivatingSubscription}
                    onClick={onReactivate}
                  />
                ) : (
                  <Button
                    label='Cancel auto-renewal'
                    icon='pi pi-ban'
                    severity='danger'
                    outlined
                    onClick={() => setIsCancelDialogVisible(true)}
                  />
                ))}
              {showYearlyUpgrade && (
                <Button
                  label='Upgrade to Yearly'
                  icon='pi pi-arrow-up-right'
                  onClick={() => openUpgradeFlow(currentPlan, 'yearly')}
                />
              )}

              {higherTierPlans.slice(0, 2).map((plan) => {
                const preferredCycle: BillingCycle = getPlanCyclePriceID(
                  plan,
                  'yearly',
                )
                  ? 'yearly'
                  : 'monthly';
                return (
                  <Button
                    key={plan.id}
                    label={`Upgrade to ${capitalizeFirstLetter(plan.name)}`}
                    icon='pi pi-arrow-up'
                    severity='secondary'
                    onClick={() => openUpgradeFlow(plan, preferredCycle)}
                  />
                );
              })}
            </div>
          </div>
        </Card>
      )}

      <DataGrid
        dataKey='id'
        entityLabel='Subscriptions'
        header={
          <div>
            <Typography type={3} className='text-2xl font-semibold mb-1'>
              Subscription records
            </Typography>
            <Typography className='text-600'>
              Active and historical subscription entries for this context.
            </Typography>
          </div>
        }
        value={loadingSubscriptions ? undefined : fullSubs}
        gridState={tableState}
        onFiltersUpdate={setTableState}
        loading={loadingSubscriptions}
        columns={[
          {
            field: 'plan.name',
            header: 'Plan',
            body: (rowData) =>
              rowData.plan?.name
                ? capitalizeFirstLetter(rowData.plan.name)
                : '',
          },
          {
            field: 'dateField',
            header: 'End Date / Renewal Date',
            sortable: true,
            body: (rowData) =>
              rowData[rowData.dateField] &&
              isValid(new Date(rowData[rowData.dateField]))
                ? format(new Date(rowData[rowData.dateField]), 'dd-MM-yyyy')
                : '',
          },
          {
            field: 'status',
            header: 'Status',
            type: 'status',
            readOnly: true,
            statusOptions: [
              {
                label: 'Active',
                value: 'active',
                severity: 'success',
              },
              {
                label: 'Blocked',
                value: 'blocked',
                severity: 'danger',
              },
              {
                label: 'Canceled',
                value: 'canceled',
                color: 'dimgray',
                textColor: 'white',
              },
              {
                label: 'Payment Failed',
                value: 'paymentFailed',
                severity: 'warning',
              },
            ],
          },
          {
            field: 'invoice',
            header: 'Invoice',
            body: (rowData: any) => {
              if (!canDisplayInvoices(rowData)) {
                return <Typography className='text-500'>Not available</Typography>;
              }
              return (
                <Button
                  label='Invoices'
                  icon='pi pi-file-pdf'
                  severity='secondary'
                  outlined
                  onClick={() => onOpenInvoices(rowData)}
                  loading={isLoadingInvoices}
                />
              );
            },
          },
          {
            field: '',
            body: (rowData: any) => (
              <div className='flex flex-0 justify-content-end align-items-center'>
                <SubscriptionOptionsMenu rowData={rowData} />
              </div>
            ),
          },
        ]}
      />
      {activeSubscription && (
        <CancelSubscriptionDialog
          visible={isCancelDialogVisible}
          planName={String(currentPlan?.name || activeSubscription?.plan?.name || '')}
          loading={isCancellingSubscription}
          onHide={() => {
            if (!isCancellingSubscription) {
              setIsCancelDialogVisible(false);
            }
          }}
          onConfirm={onConfirmCancel}
        />
      )}
      <SubscriptionInvoicesDialog
        visible={isInvoicesDialogVisible}
        loading={isLoadingInvoices}
        planName={selectedInvoicesPlanName}
        invoices={selectedInvoices}
        onHide={() => {
          if (!isLoadingInvoices) {
            setIsInvoicesDialogVisible(false);
          }
        }}
      />
    </PageContainer>
  );
}
